import { useCallback } from 'react';

import { ICartEntry } from '@rbi-ctg/menu';
import { ItemAvailabilityStatus } from 'enums/menu';
import usePosVendor from 'hooks/menu/use-pos-vendor';
import { useNavigation } from 'hooks/navigation/use-navigation';
import { useRoute } from 'hooks/navigation/use-route';
import { useCart } from 'hooks/use-cart';
import { useSwitchCartMethodDialog } from 'hooks/use-switch-cart-method-dialog';
import { useLocationContext } from 'state/location';
import { CustomEventNames, useMParticleContext } from 'state/mParticle';
import { useMenuContext } from 'state/menu';
import { useOrderContext } from 'state/order';
import { useStoreContext } from 'state/store';
import { logAddRecentItemToCartEvent } from 'utils/mParticle';
import { maybeMapCartEntryToMenuObjectIdentifier } from 'utils/menu';
import { routes } from 'utils/routing';

interface UseAddToCartProps {
  cartEntry: ICartEntry | null;
  mParticleEvent?: CustomEventNames;
}

const useAddToCartRecentItem = ({ cartEntry, mParticleEvent }: UseAddToCartProps) => {
  const { logRBIEvent } = useMParticleContext();
  const { setStoreLocatorCallbackUrl } = useLocationContext();
  const { checkItemAvailability } = useMenuContext();
  const { vendor } = usePosVendor();
  const { navigate } = useNavigation();
  const { pathname } = useRoute();
  // TODO: BKPE-1695: Migrate Pending recent item to RTK
  const {
    recent: { setPendingRecentItem, setUnavailablePendingRecentItem },
  } = useOrderContext();
  const { addItemToCart } = useCart();
  const { store, isStoreOpenAndAvailable } = useStoreContext();
  const {
    SwitchCartMethodDialog,
    openSwitchCartMethod,
    shouldShowSwitchToMobileDialog,
    // @ts-ignore
  } = useSwitchCartMethodDialog();

  const handleAddToCart = useCallback(() => {
    if (!cartEntry) {
      return;
    }
    if (isStoreOpenAndAvailable) {
      if (mParticleEvent) {
        logAddRecentItemToCartEvent(cartEntry?.name ?? '', store, logRBIEvent);
      }
      addItemToCart(cartEntry);
    } else {
      // Setting the pending recent item flag
      // The item will be added on the order context after selecting the store
      setPendingRecentItem(cartEntry);
      setStoreLocatorCallbackUrl(pathname);
      navigate(routes.storeLocator);
    }
  }, [
    addItemToCart,
    cartEntry,
    isStoreOpenAndAvailable,
    logRBIEvent,
    mParticleEvent,
    navigate,
    pathname,
    setPendingRecentItem,
    setStoreLocatorCallbackUrl,
    store,
  ]);

  const addToCart = useCallback(() => {
    if (shouldShowSwitchToMobileDialog) {
      openSwitchCartMethod({ onConfirm: handleAddToCart });
    } else {
      handleAddToCart();
    }
  }, [handleAddToCart, openSwitchCartMethod, shouldShowSwitchToMobileDialog]);

  const addToCartIfAvailable = useCallback(
    async (cartEntry: ICartEntry) => {
      const itemId = maybeMapCartEntryToMenuObjectIdentifier(cartEntry);
      if (!itemId) {
        setUnavailablePendingRecentItem(cartEntry);
        return;
      }

      const { availabilityStatus } = await checkItemAvailability({
        vendor,
        restaurantPosDataId: store.restaurantPosData?._id ?? '',
        storeNumber: store.number ?? '',
        itemId,
      });

      if (availabilityStatus !== ItemAvailabilityStatus.AVAILABLE) {
        setUnavailablePendingRecentItem(cartEntry);
      } else {
        addToCart();
      }
    },
    [
      addToCart,
      checkItemAvailability,
      setUnavailablePendingRecentItem,
      store.number,
      store.restaurantPosData?._id,
      vendor,
    ]
  );

  return {
    addToCart,
    addToCartIfAvailable,
    SwitchCartMethodDialog,
  };
};

export default useAddToCartRecentItem;
